import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { Figure, PageHeader, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { bacenRegisterParams, fetchBacenRegisters } from '@/services/bacen';
import type { AnswerOut, BacenRegisterShape } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * The instrument track: BACEN's two public registers of persons currently
 * serving a disqualification (QGI) or barred from auditing (QGP), listed
 * exactly as published, and the Copas/Copat minutes with their parse
 * coverage. A register row is a person by name and masked document; the
 * page links its PAS, never the person.
 */
export function BacenRegistersPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, percent } = useFormats();
  const label = useCodeLabel();
  const [register, setRegister] = useState<'QGI' | 'QGP'>('QGI');
  const [offset, setOffset] = useState(0);

  const { data, isPending, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['bacen', 'registers', register, offset],
    queryFn: () => fetchBacenRegisters({ register, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        page="bacen:registers"
        exportPath={`/api/v1/bacen/export/registers?${bacenRegisterParams({ register }).toString()}`}
      />

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <RegisterShapePanel answer={data.qgi} code="QGI" />
        <RegisterShapePanel answer={data.qgp} code="QGP" />
      </div>

      <section className="space-y-3" data-minutes>
        <h2 className="text-sm font-semibold text-foreground">
          {t('bacen:registers.minutes.title')}
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {data.minutes.map((m, i) => (
            <section
              key={m.value?.committee ?? i}
              className="rounded-lg border bg-card p-4"
              data-committee={m.value?.committee}
            >
              <h3 className="mb-2 text-xs font-semibold text-foreground">
                {m.value?.committee ?? t('bacen:registers.minutes.committee')}
              </h3>
              <Answer answer={m} help={t('bacen:registers.minutes.help')}>
                {(shape) => (
                  <div className="space-y-2">
                    <dl className="grid grid-cols-2 gap-2">
                      <Figure
                        label={t('bacen:registers.minutes.sessions')}
                        value={number.format(shape.sessions)}
                        second={`${shape.first_date ?? '—'} → ${shape.last_date ?? '—'}`}
                      />
                      <Figure
                        label={t('bacen:registers.minutes.processes')}
                        value={number.format(shape.processes)}
                        second={t('bacen:registers.minutes.inLedger', {
                          n: number.format(shape.processes_in_ledger),
                        })}
                      />
                      <Figure
                        label={t('bacen:registers.minutes.items')}
                        value={number.format(shape.items)}
                        second={t('bacen:registers.minutes.parsed', {
                          n: number.format(shape.items_parsed),
                          share: percent.format(shape.parsed_share),
                        })}
                      />
                      <Figure
                        label={t('bacen:registers.minutes.matched')}
                        value={number.format(shape.items_matched_row)}
                      />
                    </dl>
                    <p className="text-xs text-muted-foreground">
                      {Object.entries(shape.by_disposition)
                        .map(
                          ([code, n]) => `${label('minuteDisposition', code)} ${number.format(n)}`,
                        )
                        .join(' · ')}
                    </p>
                  </div>
                )}
              </Answer>
            </section>
          ))}
        </div>
        <FieldGuide i18nKey="bacen:registers.guide.minutes" />
      </section>

      <section
        className="rounded-lg border bg-card"
        data-register-rows
        data-stale={isPlaceholderData || undefined}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground">
          <span data-total={data.total_rows}>
            {t('bacen:registers.rows.count', {
              n: number.format(data.total_rows),
              register: label('register', register),
            })}
          </span>
          <div className="flex gap-1" role="group" aria-label={t('bacen:registers.rows.pick')}>
            {(['QGI', 'QGP'] as const).map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={register === code}
                onClick={() => {
                  setRegister(code);
                  setOffset(0);
                }}
                className={`rounded border px-2 py-0.5 ${
                  register === code ? 'bg-muted text-foreground' : 'text-muted-foreground'
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
        {data.rows.length === 0 ? (
          <div className="p-4">
            <EmptyState hint={t('bacen:registers.rows.empty')} />
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('bacen:registers.rows.pas')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:registers.rows.subject')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:registers.rows.penalty')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:registers.rows.term')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('bacen:registers.rows.start')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:registers.rows.end')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr
                    key={`${r.pas_number}-${i}`}
                    className="border-t align-top"
                    data-pas={r.pas_number}
                  >
                    <td className="px-3 py-2 tabular-nums">
                      {r.in_ledger ? (
                        <Link
                          to={`/bacen/c/${r.pas_number}`}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {r.pas_number}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">
                          {r.pas_number}
                          <span className="block text-xs">
                            {t('bacen:registers.rows.notInLedger')}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {r.subject_name}
                      <div className="text-xs tabular-nums text-muted-foreground">
                        {r.masked_doc}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{r.penalty}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {r.term_years === null ? '—' : t('bacen:cases.years', { n: r.term_years })}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {r.start_date ?? '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {r.end_date ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.total_rows > PAGE_SIZE ? (
          <div className="flex items-center justify-between border-t px-4 py-2 text-xs">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
            >
              {t('bacen:consistency.pairs.previous')}
            </button>
            <span className="text-muted-foreground">
              {t('bacen:consistency.pairs.page', {
                from: number.format(offset + 1),
                to: number.format(Math.min(offset + PAGE_SIZE, data.total_rows)),
              })}
            </span>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= data.total_rows}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
            >
              {t('bacen:consistency.pairs.next')}
            </button>
          </div>
        ) : null}
      </section>
      <FieldGuide i18nKey="bacen:registers.guide.rows" />
    </div>
  );
}

function RegisterShapePanel({
  answer,
  code,
}: {
  answer: AnswerOut<BacenRegisterShape>;
  code: 'QGI' | 'QGP';
}) {
  const { t } = useTranslation(['bacen']);
  const { number } = useFormats();
  const label = useCodeLabel();
  return (
    <section className="rounded-lg border bg-card p-4" data-register={code}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">{label('register', code)}</h2>
      <Answer answer={answer} help={t('bacen:registers.shape.help')}>
        {(s) => (
          <div className="space-y-2">
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Figure label={t('bacen:registers.shape.rows')} value={number.format(s.rows)} />
              <Figure
                label={t('bacen:registers.shape.matchedProcess')}
                value={number.format(s.matched_process)}
              />
              <Figure
                label={t('bacen:registers.shape.matchedPerson')}
                value={number.format(s.matched_person)}
              />
              <Figure
                label={t('bacen:registers.shape.endingThisYear')}
                value={number.format(s.ending_this_year)}
              />
              <Figure
                label={t('bacen:registers.shape.endingLater')}
                value={number.format(s.ending_later)}
              />
              <Figure
                label={t('bacen:registers.shape.medianYears')}
                value={
                  s.median_years === null ? '—' : t('bacen:cases.years', { n: s.median_years })
                }
              />
            </dl>
            <p className="text-xs text-muted-foreground">
              {Object.entries(s.by_penalty)
                .map(([penalty, n]) => `${penalty} ${number.format(n)}`)
                .join(' · ')}
            </p>
          </div>
        )}
      </Answer>
    </section>
  );
}
